<template>
  <div class="menu-section">
    <template v-for="item in menuItems" :key="item.name">
      <div
        v-if="item.name === 'workspaces' && item.visible"
        class="workspace-section"
      >
        <SettingsMenuItem
          :icon="item.icon"
          :label="item.label"
          :selected="isOrgRoute(organization.id, item.routeName)"
          @click="() => navigateToOrganization(organization.id, item.routeName)"
        />

        <div v-if="workspaces && workspaces.length > 0" class="workspace-items">
          <SettingsMenuItem
            v-for="workspace in workspaces"
            :key="`workspace-${workspace.id}`"
            :label="workspace.name"
            :selected="isWorkspaceRoute(organization.id, workspace.id)"
            class="workspace-item"
            @click="() => navigateToWorkspace(organization.id, workspace.id)"
          />
        </div>
      </div>

      <SettingsMenuItem
        v-else
        v-show="item.visible"
        :icon="item.icon"
        :label="item.label"
        :selected="isOrgRoute(organization.id, item.routeName)"
        :entitlement-key="item.entitlementKey"
        :organization-id="
          item.entitlementKey === 'ai-automations' ? undefined : organization.id
        "
        @click="() => navigateToOrganization(organization.id, item.routeName)"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, toRefs, type PropType } from "vue";
import { useRoute } from "vue-router";

import { FullOrganization } from "@ctrl/controller-rpc";

import AgentIcon from "@/components/icons/AgentIcon.vue";
import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import BindingIcon from "@/components/icons/BindingIcon.vue";
import CubeIcon from "@/components/icons/CubeIcon.vue";
import DashboardIcon from "@/components/icons/DashboardIcon.vue";
import HomeIcon from "@/components/icons/HomeIcon.vue";
import IntegrationIcon from "@/components/icons/IntegrationIcon.vue";
import TeamIcon from "@/components/icons/onboarding/TeamIcon.vue";
import PlansIcon from "@/components/icons/PlansIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import WorkspaceIcon from "@/components/icons/WorkspaceIcon.vue";
import * as inject from "@/components/inject";
import SettingsMenuItem from "@/components/menu/MenuItemSettings.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

interface Workspace {
  id: string;
  name: string;
}

export default defineComponent({
  name: "Organization",
  components: {
    SettingsMenuItem,
  },
  props: {
    organization: {
      type: Object as PropType<FullOrganization>,
      required: true,
    },
  },
  async setup(props) {
    const { organization } = toRefs(props);
    const orgId = computed(() => organization.value.id);
    const controller = inject.controller();
    const profile = inject.profile();
    const isBetaTester = inject.isBetaTester();
    const router = inject.router();
    const route = useRoute();

    // Check if this is the default organization (personal organization)
    const isDefaultOrganization = computed(() => organization.value.personal);

    const releaser = useReleaser();
    const [workspaces, workspaceIds, workspaceMembers] = await (async () => {
      const workspaceIds = releaser.add(
        bindAsync(orgId, async (id) => {
          if (!id) return releasableRef<string[]>([]);
          return controller.organization.workspaces.all(id);
        })
      );

      const workspaces = releaser.add(
        workspaceIds.then((ids) =>
          bindAsync(ids, async (ids) => {
            if (!ids) return releasableRef<Workspace[]>([]);

            const workspacePromises = ids.map(async (workspaceId: string) => {
              const workspaceRef = await controller.workspace.get(workspaceId);
              const workspace = workspaceRef.value;
              workspaceRef.release();
              if (!workspace) return null;
              return {
                id: workspace.id,
                name: workspace.name,
              };
            });

            const workspaces = (await Promise.all(workspacePromises))
              .filter((ws: Workspace | null): ws is Workspace => ws !== null)
              // Sort alphabetically by name
              .toSorted((a, b) => a.name.localeCompare(b.name));

            return releasableRef(workspaces);
          })
        )
      );

      const workspaceMembers = releaser.add(
        workspaces.then((wsList) =>
          bindAsync(wsList, async (wsList) => {
            if (!wsList?.length)
              return releasableRef<Record<string, string[]>>({});

            const membersPromises = wsList.map(async (ws: Workspace) => {
              const membersRef = await controller.workspace.member.all(ws.id);
              const members = membersRef.value ?? [];
              membersRef.release();
              return { id: ws.id, members };
            });

            const loaded = await Promise.all(membersPromises);
            const record: Record<string, string[]> = {};
            loaded.forEach(({ id, members }) => {
              record[id] = members;
            });
            return releasableRef(record);
          })
        )
      );

      return Promise.all([workspaces, workspaceIds, workspaceMembers]);
    })();

    const hasNoWorkspaces = computed(
      () => !workspaces.value || workspaces.value.length === 0
    );

    const belongsToAnyWorkspace = computed(() => {
      if (isDefaultOrganization.value) return true;
      const wsList = workspaces.value;
      const members = workspaceMembers.value;
      const accountId = profile.value.user.accountId;
      if (!wsList?.length || members === undefined) return false;
      return wsList.some((ws) => members[ws.id]?.includes(accountId));
    });

    const menuItems = computed(() => [
      {
        name: "home",
        label: "Home",
        icon: HomeIcon,
        routeName: RouteNames.Organization,
        visible: !isDefaultOrganization.value,
      },
      {
        name: "organization",
        label: "General",
        icon: SettingsIcon,
        routeName: RouteNames.SettingsOrganization,
        visible: true,
      },
      {
        name: "plan",
        label: "Subscription",
        icon: PlansIcon,
        routeName: RouteNames.SettingsPlan,
        visible: true,
      },
      {
        name: "integrations",
        label: "Integrations",
        icon: IntegrationIcon,
        routeName: RouteNames.SettingsIntegrations,
        visible: true,
      },
      {
        name: "types",
        label: "Types",
        icon: CubeIcon,
        routeName: RouteNames.SettingsTypes,
        visible: !hasNoWorkspaces.value && belongsToAnyWorkspace.value,
      },
      {
        name: "task-bindings",
        label: "Task Bindings",
        icon: BindingIcon,
        routeName: RouteNames.SettingsWorkspaceTaskBindings,
        visible:
          isBetaTester.value &&
          !hasNoWorkspaces.value &&
          belongsToAnyWorkspace.value,
      },
      {
        name: "people",
        label: "People",
        icon: TeamIcon,
        routeName: RouteNames.SettingsPeople,
        visible: !isDefaultOrganization.value,
      },
      {
        name: "agents",
        label: "Agents",
        icon: AgentIcon,
        routeName: RouteNames.SettingsAgents,
        visible: true,
        entitlementKey: "ai-agents",
      },
      {
        name: "automations",
        label: "Automations",
        icon: AutomationIcon,
        routeName: RouteNames.SettingsAutomations,
        visible: true,
        // Account-level: MenuItemSettings must not receive this org id.
        entitlementKey: "ai-automations",
      },
      {
        name: "studio",
        label: "Studio",
        icon: DashboardIcon,
        routeName: RouteNames.SettingsStudio,
        visible: isBetaTester.value,
      },
      {
        name: "workspaces",
        label: isDefaultOrganization.value ? "Workspace" : "Workspaces",
        icon: WorkspaceIcon,
        routeName: RouteNames.SettingsWorkspaces,
        visible: !isDefaultOrganization.value,
      },
    ]);

    const integrationRoutes = [
      RouteNames.SettingsIntegrations,
      RouteNames.SettingsIntegrationsNotion,
      RouteNames.SettingsIntegrationsNotionSetup,
      RouteNames.SettingsIntegrationsGmail,
      RouteNames.SettingsIntegrationsGmailConsent,
      RouteNames.SettingsIntegrationsGoogleWorkspace,
      RouteNames.SettingsIntegrationsGoogleCalendar,
      RouteNames.SettingsIntegrationsGoogleContacts,
      RouteNames.SettingsMembraneIntegration,
      RouteNames.SettingsMembraneAddDataCollection,
      RouteNames.SettingsMcpIntegration,
    ];

    const isOrgRoute = (orgId: string, name: RouteNames) => {
      if (name === RouteNames.SettingsTypes) {
        return route?.name === name && route?.params?.orgId === orgId;
      }
      if (name === RouteNames.SettingsIntegrations) {
        return (
          integrationRoutes.some((routeName) => routeName === route?.name) &&
          route?.params?.orgId === orgId
        );
      }
      return route?.name === name && route?.params?.orgId === orgId;
    };

    const navigateToOrganization = (orgId: string, destination: RouteNames) => {
      // Workspace-scoped pages navigate to the first workspace the user is in.
      if (
        destination === RouteNames.SettingsTypes ||
        destination === RouteNames.SettingsWorkspaceTaskBindings
      ) {
        const wsList = workspaces.value;
        const members = workspaceMembers.value;
        const accountId = profile.value.user.accountId;
        const workspaceForPage =
          wsList?.find((ws) => members?.[ws.id]?.includes(accountId)) ??
          wsList?.[0];
        if (workspaceForPage) {
          navigateTo(destination, router, {
            params: { orgId, workspaceId: workspaceForPage.id },
          });
        }
      } else {
        navigateTo(destination, router, {
          params: { orgId },
        });
      }
    };

    const navigateToWorkspace = (orgId: string, workspaceId: string) =>
      navigateTo(RouteNames.SettingsWorkspace, router, {
        params: { orgId, workspaceId },
      });

    const isWorkspaceRoute = (orgId: string, workspaceId: string) =>
      route?.name === RouteNames.SettingsWorkspace &&
      route?.params?.orgId === orgId &&
      route?.params?.workspaceId === workspaceId;

    return {
      workspaceIds,
      menuItems,
      workspaces,
      isDefaultOrganization,
      isOrgRoute,
      isWorkspaceRoute,
      navigateToOrganization,
      navigateToWorkspace,
      RouteNames,
      WorkspaceIcon,
    };
  },
});
</script>

<style scoped lang="scss">
.menu-section {
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  padding: 6px 12px 24px 12px;
}

.workspace-section {
  display: flex;
  flex-direction: column;

  .workspace-items {
    display: flex;
    flex-direction: column;
    row-gap: 2px;
    margin-top: 2px;

    :deep(.workspace-item) {
      margin-inline-start: 28px;

      .settings-menu-item {
        padding-inline-start: 4px;
      }
    }
  }
}
</style>
